<script setup>
/* eslint-disable */
import { onMounted, onBeforeUnmount, watch, ref, computed } from 'vue';
import { useHorseRacingStore } from '@/stores/horse_racing';
import { useCustomerStore } from '@/stores/customer';
import { useCurrenciesStore } from '@/stores/currencies';
import { BetSlip } from '@/stores/BetSlip';
import { useRouter } from 'vue-router';
import { openModal } from "@kolirt/vue-modal";
import Login from "@/components/modals/Login.vue";
import Register from "@/components/modals/Register.vue";

const props = defineProps({
	mobile: {
		type: Boolean,
		default: false
	},
	autoLoad: {
		type: Boolean,
		default: true
	}
});

const horseRacingStore = useHorseRacingStore();
const customerStore = useCustomerStore();
const currenciesStore = useCurrenciesStore();
const betSlipStore = BetSlip();
const router = useRouter();

const containerRef = ref(null);
const iframeRef = ref(null);
const iframe_src = ref(null);
const is_loading = ref(true);
const is_fullscreen = ref(false);
const fetch_balance_interval = ref(null);

onMounted(async () => {
	// Listen for postMessages from iframe (game events, bet placed, close, etc.)
	window.addEventListener('message', handleIframeMessage, false);

	// Listen for fullscreen state changes
	document.addEventListener('fullscreenchange', handleFullscreenChange);

	// Start 5-second balance synchronization interval while frame is active
	fetch_balance_interval.value = setInterval(() => {
		if (customerStore.customer) {
			customerStore.fetchBalance();
		}
	}, 5000);

	// If guest, don't show full-page error; show login prompt
	if (!customerStore.customer) {
		is_loading.value = false;
		return;
	}

	// Always fetch a fresh single-use authentication URL when mounting
	if (props.autoLoad) {
		await loadGame();
	}
});

onBeforeUnmount(() => {
	window.removeEventListener('message', handleIframeMessage);
	document.removeEventListener('fullscreenchange', handleFullscreenChange);

	if (fetch_balance_interval.value) {
		clearInterval(fetch_balance_interval.value);
		fetch_balance_interval.value = null;
	}

	if (document.fullscreenElement) {
		document.exitFullscreen().catch(() => {});
	}

	// Always clear stored callback URL so consumed single-use tokens are never reused
	horseRacingStore.reset();
});

// Auto-boot game when customer logs in from modal (watch ID to avoid balance re-triggers)
watch(() => customerStore.customer?.id, async (newId, oldId) => {
	if (newId && newId !== oldId) {
		horseRacingStore.error = null;
		await loadGame();
	} else if (!newId) {
		iframe_src.value = null;
		horseRacingStore.reset();
		is_loading.value = false;
	}
});

const loadGame = async () => {
	is_loading.value = true;
	horseRacingStore.reset();
	const success = await horseRacingStore.login();

	if (success) {
		iframe_src.value = horseRacingStore.iframeUrl;
	}

	is_loading.value = false;
};

const handleIframeMessage = (event) => {
	if (!event || !event.data) return;

	let data = event.data;
	if (typeof data === 'string') {
		try {
			data = JSON.parse(data);
		} catch (e) {
			// Payload is a non-JSON string
		}
	}

	// Balance / Bet Placement events: immediately sync customer balance
	if (
		data.type === 'bet_placed' ||
		data.event === 'bet' ||
		data.action === 'refresh_balance' ||
		data.action === 'balance_update'
	) {
		customerStore.fetchBalance();
	}

	// Exit / Home navigation event from iframe
	if (
		data.type === 'close_game' ||
		data.action === 'exit' ||
		data.event === 'home' ||
		data.action === 'close'
	) {
		closeGame();
	}
};

const handleIframeLoad = () => {
	is_loading.value = false;
};

const handleIframeError = () => {
	is_loading.value = false;
	horseRacingStore.error = 'Failed to load Horse Racing game. Please check your connection.';
};

const closeGame = () => {
	horseRacingStore.close();
	router.push('/');
};

const refreshGame = async () => {
	is_loading.value = true;
	iframe_src.value = null;
	horseRacingStore.reset();

	// Immediately sync balance
	if (customerStore.customer) {
		await customerStore.fetchBalance();
	}

	// Wait 100ms for iframe unmount
	await new Promise(resolve => setTimeout(resolve, 100));

	await loadGame();
};

const retryLoad = async () => {
	horseRacingStore.error = null;
	await loadGame();
};

const openLoginModal = () => {
	openModal(Login).then((data) => {
		if (data && data.show_register) {
			openRegisterModal();
		}
	}).catch(() => {});
};

const openRegisterModal = () => {
	openModal(Register).catch(() => {});
};

const toggleFullscreen = () => {
	if (!document.fullscreenElement) {
		containerRef.value?.requestFullscreen().catch(() => {});
	} else {
		document.exitFullscreen().catch(() => {});
	}
};

const handleFullscreenChange = () => {
	is_fullscreen.value = !!document.fullscreenElement;
};

// Formatted balance matching navbar layout (shown only during fullscreen)
const formattedBalance = computed(() => {
	const customer = customerStore.customer;
	if (!customer) return null;
	const code = customer.currency?.code || 'USD';
	const rawBalance = customer.balance;
	const balance = (typeof rawBalance === 'object' && rawBalance !== null)
		? (rawBalance.combined_balance ?? rawBalance.withdrawable ?? 0)
		: (rawBalance ?? 0);
	return currenciesStore.format_currency(balance, code);
});

// Disable iframe interactions when betslip is open
const iframeClasses = computed(() => {
	return {
		'horse-racing-frame': true,
		'pointer-events-none': betSlipStore.betSlipModel !== ''
	};
});
</script>

<template>
	<div ref="containerRef" class="horse-racing-container" :class="{ 'is-fullscreen': is_fullscreen }">
		<!-- Guest / Login Required State -->
		<div v-if="!customerStore.customer" class="guest-state">
			<div class="guest-card">
				<div class="guest-icon-badge">
					<span class="racing-emoji">🏇</span>
				</div>
				<h2>Horse Racing</h2>
				<p>Sign in to your account to place bets, watch races, and follow your live racing action.</p>
				<div class="guest-actions">
					<button @click="openLoginModal" class="btn-action btn-login">Log In</button>
					<button @click="openRegisterModal" class="btn-action btn-register">Register</button>
				</div>
			</div>
		</div>

		<!-- Loading State -->
		<div v-else-if="is_loading" class="loading-state">
			<div class="spinner-wrapper">
				<div class="spinner-ring"></div>
				<span class="spinner-emoji">🏇</span>
			</div>
			<h3>Connecting to Live Racing</h3>
			<p>Setting up your secure racing session...</p>
		</div>

		<!-- Error State -->
		<div v-else-if="horseRacingStore.hasError" class="error-state">
			<div class="error-icon">⚠️</div>
			<h3>Unable to Launch Racing</h3>
			<p>{{ horseRacingStore.errorMessage }}</p>
			<div class="error-actions">
				<button @click="retryLoad" class="btn-action btn-retry">Try Again</button>
				<button @click="closeGame" class="btn-action btn-back">Go Back</button>
			</div>
		</div>

		<!-- Game Frame -->
		<div v-else-if="iframe_src" class="game-frame-wrapper">
			<div class="game-header" v-if="!mobile || is_fullscreen">
				<div class="header-title-group">
					<span class="live-dot" title="Live Connection"></span>
					<h2>🐎 Horse Racing</h2>
					<span class="provider-tag">TripleApuestas</span>
				</div>

				<div class="header-actions">
					<!-- Navbar-style balance display ONLY on fullscreen -->
					<div class="fullscreen-balance" v-if="is_fullscreen && formattedBalance">
						<p class="balance-title">{{ $t ? $t('general.balance') : 'Balance' }}</p>
						<h6 class="balance-amount">{{ formattedBalance }}</h6>
					</div>

					<button @click="refreshGame" class="btn-header btn-refresh" title="Refresh Session & Balance">
						<span class="icon">🔄</span>
						<span class="text">Refresh</span>
					</button>
					<button @click="toggleFullscreen" class="btn-header btn-fullscreen" :title="is_fullscreen ? 'Exit Fullscreen' : 'Fullscreen'">
						<span class="icon">{{ is_fullscreen ? '⛶' : '⛶' }}</span>
						<span class="text">{{ is_fullscreen ? 'Exit' : 'Fullscreen' }}</span>
					</button>
					<button @click="closeGame" class="btn-header btn-close" title="Close Game">
						<span class="icon">✕</span>
					</button>
				</div>
			</div>

			<iframe
				ref="iframeRef"
				:class="iframeClasses"
				:src="iframe_src"
				@load="handleIframeLoad"
				@error="handleIframeError"
				allowfullscreen
			></iframe>
		</div>
	</div>
</template>

<style scoped>
.horse-racing-container {
	width: 100%;
	display: flex;
	flex-direction: column;
	background: #0f1218;
	position: relative;
	border-radius: 8px;
	overflow: hidden;
}

.horse-racing-container.is-fullscreen {
	border-radius: 0;
	min-height: 100vh;
	width: 100vw;
	height: 100vh;
}

/* =========================================================================
   Guest State
   ========================================================================= */
.guest-state {
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 550px;
	padding: 2rem;
	background: radial-gradient(circle at center, #1b2230 0%, #0e121a 100%);
}

.guest-card {
	max-width: 440px;
	width: 100%;
	padding: 2.5rem 2rem;
	text-align: center;
	background: rgba(255, 255, 255, 0.03);
	border: 1px solid rgba(255, 255, 255, 0.08);
	border-radius: 12px;
	box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
	backdrop-filter: blur(8px);
}

.guest-icon-badge {
	width: 72px;
	height: 72px;
	margin: 0 auto 1.5rem;
	border-radius: 50%;
	background: linear-gradient(135deg, rgba(255, 215, 0, 0.2), rgba(255, 215, 0, 0.05));
	border: 1px solid rgba(255, 215, 0, 0.3);
	display: flex;
	align-items: center;
	justify-content: center;
}

.racing-emoji {
	font-size: 2.2rem;
}

.guest-card h2 {
	margin: 0 0 0.75rem;
	font-size: 1.6rem;
	font-weight: 700;
	color: #fff;
}

.guest-card p {
	margin: 0 0 1.75rem;
	font-size: 0.95rem;
	color: rgba(255, 255, 255, 0.7);
	line-height: 1.5;
}

.guest-actions {
	display: flex;
	gap: 0.75rem;
	justify-content: center;
}

/* =========================================================================
   Loading State
   ========================================================================= */
.loading-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-height: 550px;
	padding: 2rem;
	color: #fff;
	background: radial-gradient(circle at center, #171d28 0%, #0d1117 100%);
}

.spinner-wrapper {
	position: relative;
	width: 80px;
	height: 80px;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 1.5rem;
}

.spinner-ring {
	position: absolute;
	width: 100%;
	height: 100%;
	border: 3px solid rgba(255, 215, 0, 0.15);
	border-top-color: #ffd700;
	border-radius: 50%;
	animation: spin 1s cubic-bezier(0.68, -0.55, 0.27, 1.55) infinite;
}

.spinner-emoji {
	font-size: 2rem;
	animation: pulse 1.5s ease-in-out infinite;
}

.loading-state h3 {
	margin: 0 0 0.5rem;
	font-size: 1.3rem;
	font-weight: 600;
	color: #fff;
}

.loading-state p {
	margin: 0;
	font-size: 0.9rem;
	color: rgba(255, 255, 255, 0.6);
}

@keyframes spin {
	to { transform: rotate(360deg); }
}

@keyframes pulse {
	0%, 100% { transform: scale(1); opacity: 1; }
	50% { transform: scale(1.15); opacity: 0.8; }
}

/* =========================================================================
   Error State
   ========================================================================= */
.error-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-height: 500px;
	padding: 2rem;
	color: #fff;
	text-align: center;
	background: rgba(220, 53, 69, 0.05);
}

.error-icon {
	font-size: 3rem;
	margin-bottom: 1rem;
}

.error-state h3 {
	margin: 0 0 0.75rem;
	font-size: 1.4rem;
	color: #ff6b6b;
}

.error-state p {
	margin: 0 0 1.5rem;
	max-width: 420px;
	font-size: 0.95rem;
	color: rgba(255, 255, 255, 0.75);
}

.error-actions {
	display: flex;
	gap: 0.75rem;
}

/* =========================================================================
   Buttons
   ========================================================================= */
.btn-action {
	padding: 0.7rem 1.4rem;
	border-radius: 6px;
	font-size: 0.95rem;
	font-weight: 600;
	cursor: pointer;
	border: 1px solid transparent;
	transition: all 0.2s ease;
}

.btn-login {
	background: #ffd700;
	color: #0d1117;
}

.btn-login:hover {
	background: #ffea60;
	transform: translateY(-1px);
}

.btn-register {
	background: rgba(255, 255, 255, 0.08);
	color: #fff;
	border-color: rgba(255, 255, 255, 0.15);
}

.btn-register:hover {
	background: rgba(255, 255, 255, 0.15);
}

.btn-retry {
	background: #ffd700;
	color: #0d1117;
}

.btn-retry:hover {
	background: #ffea60;
}

.btn-back {
	background: rgba(255, 255, 255, 0.1);
	color: #fff;
}

.btn-back:hover {
	background: rgba(255, 255, 255, 0.18);
}

/* =========================================================================
   Game Frame Wrapper & Header Bar
   ========================================================================= */
.game-frame-wrapper {
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
	flex: 1;
}

.game-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 0.75rem 1.25rem;
	background: #141922;
	border-bottom: 1px solid rgba(255, 255, 255, 0.08);
	z-index: 10;
}

.header-title-group {
	display: flex;
	align-items: center;
	gap: 0.6rem;
}

.live-dot {
	width: 9px;
	height: 9px;
	background: #28a745;
	border-radius: 50%;
	box-shadow: 0 0 8px #28a745;
	animation: pulse-dot 2s infinite;
}

@keyframes pulse-dot {
	0%, 100% { opacity: 1; transform: scale(1); }
	50% { opacity: 0.5; transform: scale(0.85); }
}

.game-header h2 {
	margin: 0;
	font-size: 1.15rem;
	font-weight: 700;
	color: #ffd700;
}

.provider-tag {
	font-size: 0.7rem;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	padding: 2px 6px;
	border-radius: 4px;
	background: rgba(255, 255, 255, 0.08);
	color: rgba(255, 255, 255, 0.7);
}

.header-actions {
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

/* =========================================================================
   Fullscreen Navbar-Style Balance
   ========================================================================= */
.fullscreen-balance {
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	justify-content: center;
	margin-right: 0.5rem;
	padding-right: 0.85rem;
	border-right: 1px solid rgba(255, 255, 255, 0.15);
	line-height: 1.15;
}

.fullscreen-balance .balance-title {
	margin: 0;
	font-size: 0.68rem;
	font-weight: 300;
	color: rgba(255, 255, 255, 0.6);
	text-transform: capitalize;
}

.fullscreen-balance .balance-amount {
	margin: 0;
	font-size: 0.92rem;
	font-weight: 600;
	color: #fff;
}

.btn-header {
	display: inline-flex;
	align-items: center;
	gap: 0.35rem;
	padding: 0.4rem 0.75rem;
	font-size: 0.82rem;
	font-weight: 600;
	border-radius: 6px;
	cursor: pointer;
	border: 1px solid rgba(255, 255, 255, 0.1);
	background: rgba(255, 255, 255, 0.05);
	color: #fff;
	transition: all 0.2s ease;
}

.btn-header:hover {
	background: rgba(255, 255, 255, 0.12);
	border-color: rgba(255, 255, 255, 0.25);
}

.btn-header .icon {
	font-size: 0.95rem;
}

.btn-header.btn-close {
	padding: 0.4rem 0.6rem;
	background: rgba(220, 53, 69, 0.15);
	border-color: rgba(220, 53, 69, 0.3);
	color: #ff6b6b;
}

.btn-header.btn-close:hover {
	background: rgba(220, 53, 69, 0.3);
	border-color: rgba(220, 53, 69, 0.5);
}

/* =========================================================================
   Iframe
   ========================================================================= */
.horse-racing-frame {
	width: 100%;
	min-height: 720px;
	height: 600px;
	border: none;
	background: #0f1218;
}

.horse-racing-container.is-fullscreen .horse-racing-frame {
	height: calc(100vh - 50px);
	min-height: calc(100vh - 50px);
}

.pointer-events-none {
	pointer-events: none;
}

/* =========================================================================
   Mobile Viewport
   ========================================================================= */
@media (max-width: 768px) {
	.horse-racing-container:not(.is-fullscreen) {
		min-height: auto;
		border-radius: 0;
	}

	.btn-header .text {
		display: none;
	}

	.btn-header {
		padding: 0.4rem 0.55rem;
	}

	.horse-racing-frame:not(.is-fullscreen) {
		min-height: 560px;
		height: 720px;
	}
}
</style>
